<template>
  <AppCard ref="root" as="section" class="verdict" :aria-labelledby="titleId">
    <SectionCaption as="p" inset class="context">{{
      t('verdict.context', { when: day, place })
    }}</SectionCaption>
    <h2 :id="titleId" ref="title" class="question" tabindex="-1">
      {{ card.name }}<br />{{ t('verdict.question_tail') }}
    </h2>

    <RatingScale v-model="score" :current="returnedWith" />

    <AppField
      v-model="review"
      class="review"
      kind="multiline"
      rows="3"
      :maxlength="REVIEW_MAX"
      :label="t('verdict.review_label')"
      :placeholder="t('verdict.review_placeholder')"
      :error="error"
      :error-text="unsupported ? t('verdict.review_unsupported') : null"
    />

    <AppButton class="save" :inactive="score === null" block @click="save">
      {{ score === null ? t('verdict.save_disabled') : t('verdict.save') }}
    </AppButton>
    <AppButton variant="ghost" block @click="$emit('skip')">
      {{ t('verdict.skip') }}
    </AppButton>
  </AppCard>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, useId, watch } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { ERROR, ratingSchema, tidyText } from '@molvia/model'
import type { ErrorCode, PendingVerdict, WireCode } from '@molvia/model'
import AppButton from '@/components/AppButton.vue'
import AppCard from '@/components/AppCard.vue'
import AppField from '@/components/AppField.vue'
import RatingScale from '@/components/RatingScale.vue'
import { placeLabel } from '@/components/placeLabel'
import { dayOfAnyYear } from '@/days'
import { SCORES } from '@/components/rating'
import SectionCaption from '@/components/SectionCaption.vue'
import { useFieldRefusal } from '@/composables/useFieldRefusal'
import type { Score, VerdictDraft } from '@/stores/verdictDrafts'

/** The review's own bound (`newVerdictSchema`), kept by the field so it cannot be passed. */
const REVIEW_MAX = 500

const DOMAIN_CODES: readonly string[] = Object.values(ERROR)

/**
 * A refusal the domain has words for is shown by its code. An `issue.*` is a message to a
 * developer (MOL-18); for the review it means only that some character in it cannot be kept,
 * and the card says that in its own words (owner's decision, G2).
 */
function domainCode(code: WireCode | null | undefined): ErrorCode | null {
  return code && DOMAIN_CODES.includes(code) ? (code as ErrorCode) : null
}

/**
 * One question of «Оценки»: this item, bought there and then — how was it? (MOL-28, Р-4).
 *
 * Digits, not stars: five stars are hard to hit with a thumb and read as decoration, and here
 * the score is the entry into a verdict. A second tap on the chosen digit takes it back.
 *
 * «Сохранить» is not `disabled` without a score — it has to be reachable and heard — but the
 * kit's not now (`inactive`, Ф-6): its words say what is missing, and a tap does nothing. A card
 * the server refused comes back with its score said over the scale (6d, Ф-25). The review is
 * checked by the same schema the server uses, for the person's sake only: the server decides. A
 * card is keyed by its item, so everything typed here is the card's own and goes with it.
 */
export default defineComponent({
  name: 'VerdictCard',
  components: { AppButton, AppCard, AppField, RatingScale, SectionCaption },
  props: {
    card: { type: Object as PropType<PendingVerdict>, required: true },
    /** What the phone kept for this item: words typed before, or a refusal to show. */
    draft: { type: Object as PropType<VerdictDraft | undefined>, default: undefined },
    /** Take the focus on arrival — after the previous card was saved or put off. */
    focusOnMount: { type: Boolean, default: false },
    /**
     * The city of the place, where another card of the queue names a shop of that name in another
     * city (MOL-120) — the queue decides, since the card alone cannot know.
     */
    city: { type: String as PropType<string | null>, default: null },
    /** The screen's day (`useLocalDay`): «сегодня» and «вчера» of the purchase are said from it (MOL-329). */
    today: { type: String, required: true },
  },
  emits: {
    save: (score: Score, review: string) =>
      (SCORES as readonly number[]).includes(score) && typeof review === 'string',
    skip: () => true,
    change: (score: Score | null, review: string) =>
      (score === null || (SCORES as readonly number[]).includes(score)) &&
      typeof review === 'string',
  },
  setup(props, { emit }) {
    const i18n = useI18n()
    const { t, locale } = i18n
    const titleId = useId()
    const title = ref<HTMLElement | null>(null)
    // A review refused is heard (MOL-235, MOL-333): on a screen, so in the app's region.
    const root = ref<{ $el: Element } | null>(null)
    const refuse = useFieldRefusal()

    const score = ref<Score | null>(props.draft?.score ?? null)
    const review = ref(props.draft?.review ?? '')
    const error = ref<ErrorCode | null>(domainCode(props.draft?.error))
    /** The review holds a character the server will not keep: the tab and its kin are tidied. */
    // Only with a review to blame: a refusal of an empty one is not about a character in it
    // (self-review С-19), and says the generic words instead.
    const refusedCode = Boolean(props.draft?.error) && error.value === null
    const unsupported = ref(refusedCode && Boolean(props.draft?.review.trim()))
    if (refusedCode && !unsupported.value) error.value = ERROR.INTERNAL
    // The score the card came back refused with, kept while it is on screen: the point a change is
    // made from. Touched and left, the draft has no refusal any more, and nothing is said again.
    const returnedWith = props.draft?.error ? props.draft.score : null
    let saved = false

    const day = computed(() => dayOfAnyYear(props.card.boughtAt, locale.value, props.today))
    const place = computed(() => placeLabel(props.card.placeName, props.city, i18n))

    // Synchronous, so an error set right after an edit in `save` is not cleared by it later.
    watch(
      [score, review],
      ([nextScore, nextReview]) => {
        error.value = null
        unsupported.value = false
        if (!saved) emit('change', nextScore, nextReview)
      },
      { flush: 'sync' },
    )

    function save(): void {
      if (saved) return
      // The kit cancels a tap on «Выберите оценку»; this is for a call from anywhere else.
      if (score.value === null) return
      // Tidied first, asked about after: a review of invisible marks is as empty as one of
      // spaces, and goes without a review the same way (self-review С-16).
      const tidied = tidyText(review.value)
      review.value = tidied.trim() ? tidied : ''
      if (review.value && !ratingSchema.shape.review.safeParse(review.value).success) {
        unsupported.value = true
        void refuse(() => root.value?.$el.querySelector<HTMLElement>('[aria-invalid="true"]'))
        return
      }
      // One save per card: a double tap would otherwise hand the next card the second tap.
      saved = true
      emit('save', score.value, review.value)
    }

    onMounted(() => {
      if (props.focusOnMount) title.value?.focus({ preventScroll: true })
    })

    return {
      t,
      REVIEW_MAX,
      titleId,
      title,
      root,
      score,
      review,
      error,
      unsupported,
      returnedWith,
      day,
      place,
      save,
    }
  },
})
</script>

<style scoped lang="scss">
.verdict {
  padding: var(--space-6) var(--space-4);
}

.question {
  @include display-type;

  margin: var(--space-2) 0 var(--space-6);
  font-size: var(--text-title);
  line-height: var(--leading-snug);
  overflow-wrap: anywhere;

  &:focus {
    outline: none;
  }
}

.review {
  margin-top: var(--space-6);
}

.save {
  margin-top: var(--space-6);
}
</style>
